import { api } from "./api.js";
import type { InvoiceStatus } from "./sales.js";

/**
 * Cross-invoice payments ledger (Sales -> Payments). Recording a payment still
 * happens on the invoice (`addPayment` in ./sales.ts); this is the flat list.
 */

export interface PaymentRow {
  id: string;
  invoiceId: string;
  invoice?: { id: string; number: string; totalCents: number; status: InvoiceStatus } | null;
  clientId: string;
  client?: { id: string; name: string; kind: "company" | "person" } | null;
  amountCents: number;
  currency: string;
  method: string | null;
  reference: string | null;
  paidAt: string;
  note: string | null;
  createdAt: string;
}

export interface PaymentsPage {
  rows: PaymentRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  /** Sum over the whole filtered set, not just the current page. */
  sumCents: number;
}

export interface PaymentsQuery {
  q?: string;
  clientId?: string;
  invoiceId?: string;
  method?: string;
  /** Inclusive `paidAt` bounds as YYYY-MM-DD. */
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

function qs(params?: PaymentsQuery): string {
  if (!params) return "";
  const p = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return p.length ? `?${p.join("&")}` : "";
}

export const getPayments = (params?: PaymentsQuery) => api.get<PaymentsPage>(`/admin/crm/payments${qs(params)}`);
export const getPayment = (id: string) => api.get<PaymentRow>(`/admin/crm/payments/${id}`);
export const getPaymentMethods = () => api.get<{ rows: string[] }>("/admin/crm/payment-methods");
export const deletePaymentById = (id: string) => api.del<{ ok: boolean }>(`/admin/crm/payments/${id}`);
